import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, InputAdornment, Link, Stack, TextField } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import useAuth from '../../auth/useAuth';
import { PATH_AFTER_LOGIN } from '../../config';
import { applyServerErrors, getErrorMessage } from '../../utils/errorHandler';

const schema = yup.object({
  email: yup.string().email('Ingresa un correo válido').required('El correo es obligatorio'),
  password: yup.string().required('La contraseña es obligatoria'),
});

export default function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data) => {
    setFormError('');
    try {
      const user = await login(data);
      const redirectTo = location.state?.from?.pathname || PATH_AFTER_LOGIN[user.role] || '/';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        setFormError(getErrorMessage(error, 'Correo o contraseña incorrectos.'));
      }
    }
  };

  return (
    <Stack component="form" spacing={3} onSubmit={handleSubmit(onSubmit)} noValidate>
      {formError && <Alert severity="error">{formError}</Alert>}

      <TextField
        label="Correo electrónico"
        type="email"
        autoComplete="email"
        error={Boolean(errors.email)}
        helperText={errors.email?.message}
        {...register('email')}
      />

      <TextField
        label="Contraseña"
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        error={Boolean(errors.password)}
        helperText={errors.password?.message}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        {...register('password')}
      />

      <Link component={RouterLink} to="/recuperar-password" variant="body2" sx={{ alignSelf: 'flex-end' }}>
        ¿Olvidaste tu contraseña?
      </Link>

      <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
        {isSubmitting ? 'Ingresando...' : 'Iniciar sesión'}
      </Button>
    </Stack>
  );
}
